import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { List, Ticket, UserProfile } from '@/lib/types'
import {
  cn,
  displayListName,
  formatRelative,
  groupMedia,
  isTicketUrgent,
  labelColor,
  listDotClass,
} from '@/lib/utils'
import { htmlToText, sanitizeRichHtml } from '@/lib/richtext'
import { supabase } from '@/lib/supabase'
import i18n from '@/i18n'
import { AvatarStack } from './AvatarStack'
import { MediaPreviewChip } from './MediaPreviewChip'

/** Wrap a plain-text paste into a safe HTML paragraph when the clipboard
 *  carried no HTML payload (preserves line breaks). */
function escapeHtmlText(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\n/g, '<br>')
}
import {
  Check,
  Flame,
  HelpCircle,
  CalendarDays,
  MessagesSquare,
  MessageCircleMore,
  Star,
  Paperclip,
  Inbox,
  Plus,
  X,
  Loader2,
  Sparkles,
} from 'lucide-react'

interface Props {
  lists: List[]
  /** Already board-filtered tickets (search / AI / labels / time applied). */
  tickets: Ticket[]
  profilesById: Record<string, UserProfile>
  /** Map ticket_id → set of subscribed user_ids, so the row can show the
   *  full roster (assignee + subscribers) like a Kanban card does. */
  subsByTicket: Map<string, Set<string>>
  newTicketIds: Set<string>
  /** List ids currently SHOWN. A card whose list isn't in here is hidden.
   *  Lists behave like toggleable category tags in this view. */
  activeListIds: Set<string>
  onToggleList: (listId: string) => void
  /** Re-show every category at once. */
  onShowAllLists: () => void
  /** Intake (« Reçu ») list id — where a card added from this view lands
   *  by default. Null when the project has no intake list. */
  intakeListId: string | null
  /** Create a card in the given list (with an optional rich-HTML
   *  description, e.g. staged from a paste). */
  onCreateCard: (
    listId: string,
    title: string,
    description?: string,
  ) => void | Promise<void>
  /** Open the full-screen media viewer (lightbox) for the given URLs —
   *  fired by the image / video chips. */
  onViewMedia: (urls: string[]) => void
  onTicketClick: (ticket: Ticket) => void
}

/** Most-recent activity timestamp of a card — the anchor used to sort the
 *  stack newest-first. Considers arrival, last reply and last comment so a
 *  card bubbles up whenever anything happens on it. */
function activityMs(t: Ticket): number {
  const stamps = [t.source_date ?? t.created_at, t.last_reply_at, t.last_comment_at]
    .filter(Boolean)
    .map((s) => new Date(s as string).getTime())
    .filter((n) => !Number.isNaN(n))
  return stamps.length ? Math.max(...stamps) : new Date(t.created_at).getTime()
}

/** Mail-style board: every card is a single row, newest at the top, oldest
 *  as you scroll down — a chronological reading of the whole channel. The
 *  Kanban lists become toggleable category tags at the top so the user can
 *  narrow the stack to « Reçu + À vérifier », etc. */
export function BoardListView({
  lists,
  tickets,
  profilesById,
  subsByTicket,
  newTicketIds,
  activeListIds,
  onToggleList,
  onShowAllLists,
  intakeListId,
  onCreateCard,
  onViewMedia,
  onTicketClick,
}: Props) {
  const { t } = useTranslation()
  const [composerOpen, setComposerOpen] = useState(false)
  const [draft, setDraft] = useState('')
  // Rich paste staging: the FULL pasted HTML becomes the new card's
  // description; the AI returns a short title (prefills the input) +
  // summary (italic lead). Mirrors the Kanban composer behaviour.
  const [pendingDescription, setPendingDescription] = useState<string | null>(
    null,
  )
  const [summarizing, setSummarizing] = useState(false)
  const composerRef = useRef<HTMLTextAreaElement | null>(null)
  useEffect(() => {
    if (composerOpen) composerRef.current?.focus()
  }, [composerOpen])

  async function submitDraft() {
    const title = draft.trim()
    if (!title || !intakeListId) {
      setComposerOpen(false)
      setDraft('')
      setPendingDescription(null)
      return
    }
    await onCreateCard(intakeListId, title, pendingDescription ?? undefined)
    setDraft('')
    setPendingDescription(null)
    // Keep the composer open so the user can keep adding cards quickly.
    composerRef.current?.focus()
  }

  /** Intercept a "big" paste in the composer: stage the full pasted content
   *  (rich HTML if present, so colours / bold / highlights survive) as the
   *  card description, then ask Claude for a short title + summary that
   *  prefill the input. Small pastes flow through untouched. */
  async function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const html = e.clipboardData.getData('text/html')
    const plain = e.clipboardData.getData('text/plain')
    const text = (plain || '').replace(/\r\n/g, '\n')
    const isBigPaste = text.length >= 80 || /\n/.test(text)
    if (!isBigPaste) return
    e.preventDefault()
    const htmlPart = html ? sanitizeRichHtml(html) : null
    const stagedHtml = htmlPart || `<p>${escapeHtmlText(text)}</p>`
    setPendingDescription(stagedHtml)
    if (!draft.trim()) setDraft(t('list.composerSummarizing'))
    setSummarizing(true)
    try {
      const { data, error } = await supabase.functions.invoke('summarize-paste', {
        body: { text, locale: i18n.language },
      })
      if (error) throw new Error(error.message)
      const result = data as { ok?: boolean; title?: string; summary?: string }
      const aiTitle = (result.title ?? '').trim()
      const aiSummary = (result.summary ?? '').trim()
      setDraft((cur) =>
        aiTitle
          ? aiTitle
          : cur === t('list.composerSummarizing')
            ? ''
            : cur,
      )
      if (aiSummary) {
        const lead = `<p><em>${escapeHtmlText(aiSummary)}</em></p>`
        setPendingDescription((cur) => (cur ? `${lead}${cur}` : `${lead}${stagedHtml}`))
      }
    } catch (err) {
      console.error('summarize-paste failed', err)
      setDraft((cur) => (cur === t('list.composerSummarizing') ? '' : cur))
    } finally {
      setSummarizing(false)
      composerRef.current?.focus()
    }
  }
  const listById = useMemo(() => {
    const m = new Map<string, List>()
    for (const l of lists) m.set(l.id, l)
    return m
  }, [lists])

  // Per-list counts over the full (board-filtered) set — stable as the
  // user toggles categories, so the chips don't flicker their numbers.
  const countByList = useMemo(() => {
    const c: Record<string, number> = {}
    for (const tk of tickets) c[tk.list_id] = (c[tk.list_id] ?? 0) + 1
    return c
  }, [tickets])

  const rows = useMemo(
    () =>
      tickets
        .filter((tk) => activeListIds.has(tk.list_id))
        .map((tk) => ({ tk, at: activityMs(tk) }))
        .sort((a, b) => b.at - a.at),
    [tickets, activeListIds],
  )

  const someHidden = lists.some((l) => !activeListIds.has(l.id))

  return (
    <div className="flex h-full flex-col">
      {/* Category tags — toggle which lists feed the stack. */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--color-border-soft)] bg-[var(--color-surface)] px-4 py-2.5">
        <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
          {t('listView.categories')}
        </span>
        {lists.map((l) => {
          const on = activeListIds.has(l.id)
          const count = countByList[l.id] ?? 0
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => onToggleList(l.id)}
              aria-pressed={on}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors',
                on
                  ? 'border-[var(--color-border)] bg-[var(--color-surface-2)] text-[var(--color-text)]'
                  : 'border-[var(--color-border-soft)] bg-transparent text-[var(--color-text-dim)] opacity-60 hover:opacity-100',
              )}
            >
              <span className={cn('h-2 w-2 shrink-0 rounded-full', listDotClass(l))} />
              <span className="truncate">{displayListName(l)}</span>
              <span className="tabular-nums text-[10.5px] text-[var(--color-text-dim)]">
                {count}
              </span>
              {on && <Check className="h-3 w-3 text-[var(--color-accent)]" strokeWidth={3} />}
            </button>
          )
        })}
        {someHidden && (
          <button
            type="button"
            onClick={onShowAllLists}
            className="ml-1 text-[11px] font-medium text-[var(--color-link)] transition-colors hover:underline"
          >
            {t('listView.showAll')}
          </button>
        )}
      </div>

      {/* Quick add — a card created here lands in « Reçu » (intake) and,
          since it's the freshest, sits at the very top of the stack. The
          user can re-file it later from its detail modal or in Kanban. */}
      {intakeListId && (
        <div className="border-b border-[var(--color-border-soft)] bg-[var(--color-surface)] px-4 py-2">
          {composerOpen ? (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-2 shadow-sm">
              <textarea
                ref={composerRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    void submitDraft()
                  }
                  if (e.key === 'Escape') {
                    setComposerOpen(false)
                    setDraft('')
                    setPendingDescription(null)
                  }
                }}
                rows={2}
                placeholder={t('listView.addPlaceholder')}
                className="w-full resize-none bg-transparent px-1 py-0.5 text-[13px] text-[var(--color-text)] outline-none"
              />
              <div className="mt-1 flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1 text-[11px] text-[var(--color-text-dim)]">
                  {summarizing ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {t('list.composerSummarizing')}
                    </>
                  ) : pendingDescription ? (
                    <>
                      <Sparkles className="h-3 w-3 text-[var(--color-accent)]" />
                      {t('listView.addRichStaged')}
                    </>
                  ) : (
                    <>
                      <Inbox className="h-3 w-3" />
                      {t('listView.addHint')}
                    </>
                  )}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setComposerOpen(false)
                      setDraft('')
                    }}
                    className="rounded-md p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                    aria-label={t('common.cancel')}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitDraft()}
                    disabled={!draft.trim()}
                    className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-accent)] px-2.5 py-1 text-[12px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:opacity-40"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('listView.add')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setComposerOpen(true)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] font-medium text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
            >
              <Plus className="h-4 w-4" />
              {t('listView.addCard')}
            </button>
          )}
        </div>
      )}

      {/* The stack. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-[var(--color-text-dim)]">
            <Inbox className="h-8 w-8 opacity-40" />
            <p className="text-[13px]">{t('listView.empty')}</p>
          </div>
        ) : (
          <ul>
            {rows.map(({ tk, at }) => {
              const list = listById.get(tk.list_id)
              const isNew = newTicketIds.has(tk.id)
              const snippet = htmlToText(tk.description ?? '').slice(0, 160)
              const urgent = isTicketUrgent(tk) || tk.priority === 'urgent'
              const media = groupMedia(tk.media_urls ?? [])
              // Audio / pdf / generic files keep the paperclip; images and
              // videos get their own hoverable preview chips.
              const otherAttachments =
                media.audio.length + media.pdf.length + media.file.length
              const comments = tk.comments_count ?? 0
              return (
                <li key={tk.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => onTicketClick(tk)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onTicketClick(tk)
                      }
                    }}
                    className={cn(
                      'group flex w-full cursor-pointer items-center gap-3 border-b border-[var(--color-border-soft)] px-4 py-2.5 text-left transition-colors hover:bg-[var(--color-surface-2)]',
                      isNew && 'bg-[var(--color-accent-bg)]/30',
                    )}
                  >
                    {/* Unread dot */}
                    <span
                      className={cn(
                        'h-2 w-2 shrink-0 rounded-full',
                        isNew ? 'bg-[var(--color-accent)]' : 'bg-transparent',
                      )}
                    />

                    {/* Category badge — fixed width so titles align like an inbox */}
                    <span className="hidden w-32 shrink-0 items-center gap-1.5 sm:flex">
                      <span
                        className={cn(
                          'h-2 w-2 shrink-0 rounded-full',
                          list ? listDotClass(list) : 'bg-[var(--color-text-dim)]',
                        )}
                      />
                      <span className="truncate text-[11px] font-medium text-[var(--color-text-dim)]">
                        {list ? displayListName(list) : '—'}
                      </span>
                    </span>

                    {/* Title + snippet */}
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span
                          className={cn(
                            'truncate text-[13px]',
                            isNew
                              ? 'font-semibold text-[var(--color-text-strong)]'
                              : 'font-medium text-[var(--color-text)]',
                          )}
                        >
                          {tk.title}
                        </span>
                        {(tk.labels ?? []).slice(0, 2).map((l) => (
                          <span
                            key={l}
                            className={cn(
                              'hidden shrink-0 rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold ring-1 ring-inset md:inline',
                              labelColor(l),
                            )}
                          >
                            {l}
                          </span>
                        ))}
                        {(tk.labels?.length ?? 0) > 2 && (
                          <span className="hidden shrink-0 text-[10px] text-[var(--color-text-dim)] md:inline">
                            +{(tk.labels?.length ?? 0) - 2}
                          </span>
                        )}
                      </span>
                      {snippet && (
                        <span className="mt-0.5 block truncate text-[11.5px] text-[var(--color-text-dim)]">
                          {snippet}
                        </span>
                      )}
                    </span>

                    {/* Status icons */}
                    <span className="flex shrink-0 items-center gap-1.5 text-[var(--color-text-dim)]">
                      {urgent && <Flame className="h-3.5 w-3.5 text-rose-500" strokeWidth={2.5} />}
                      {tk.ai_is_question && (
                        <HelpCircle className="h-3.5 w-3.5 text-amber-500" strokeWidth={2.5} />
                      )}
                      {tk.ai_mentioned_date && (
                        <CalendarDays className="h-3.5 w-3.5 text-sky-500" strokeWidth={2.5} />
                      )}
                      {tk.awaiting_reply && (
                        <MessagesSquare
                          className="h-3.5 w-3.5 text-emerald-500"
                          fill="currentColor"
                          strokeWidth={1.5}
                        />
                      )}
                      {comments > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-sky-500">
                          <MessageCircleMore className="h-3.5 w-3.5" strokeWidth={2.2} />
                          <span className="text-[10px] font-semibold tabular-nums">{comments}</span>
                        </span>
                      )}
                      {/* Image / video chips — distinct icons + hover preview
                          (muted autoplay for video), opening the lightbox on
                          click. Other attachments keep the paperclip. */}
                      {media.image.length > 0 && (
                        <MediaPreviewChip
                          kind="image"
                          urls={media.image}
                          label={t('card.viewImages')}
                          countTitle={t('card.viewImagesCount', {
                            count: media.image.length,
                          })}
                          onView={() => onViewMedia(media.image)}
                        />
                      )}
                      {media.video.length > 0 && (
                        <MediaPreviewChip
                          kind="video"
                          urls={media.video}
                          label={t('card.viewVideos')}
                          countTitle={t('card.viewVideosCount', {
                            count: media.video.length,
                          })}
                          onView={() => onViewMedia(media.video)}
                        />
                      )}
                      {otherAttachments > 0 && (
                        <span
                          className="inline-flex items-center gap-0.5"
                          title={t('detail.attachments')}
                        >
                          <Paperclip className="h-3.5 w-3.5" />
                          {otherAttachments > 1 && (
                            <span className="text-[10px] font-semibold tabular-nums">
                              {otherAttachments}
                            </span>
                          )}
                        </span>
                      )}
                      {tk.favorite && (
                        <Star className="h-3.5 w-3.5 text-amber-400" fill="currentColor" strokeWidth={1.5} />
                      )}
                    </span>

                    {/* Relative date */}
                    <span className="w-14 shrink-0 text-right text-[11px] tabular-nums text-[var(--color-text-dim)]">
                      {formatRelative(new Date(at).toISOString())}
                    </span>

                    {/* Roster — assignee (accent ring) + every subscriber,
                        same overlapping stack as the Kanban card. */}
                    {(() => {
                      const members: string[] = []
                      if (tk.assignee_id) members.push(tk.assignee_id)
                      const subs = subsByTicket.get(tk.id)
                      if (subs) {
                        for (const id of subs) {
                          if (!members.includes(id)) members.push(id)
                        }
                      }
                      return (
                        <span className="flex w-16 shrink-0 justify-end">
                          {members.length > 0 && (
                            <AvatarStack
                              userIds={members}
                              profilesById={profilesById}
                              assigneeId={tk.assignee_id ?? null}
                              max={4}
                              size="md"
                            />
                          )}
                        </span>
                      )
                    })()}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
